import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import perplexityService from '../../../services/perplexityService';
import toast from '../../../components/ui/Toast';

const IndustryTrends = ({ userId }) => {
  const [loading, setLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('saas');
  const [trendInsights, setTrendInsights] = useState(null);

  const industries = [
    { value: 'saas', label: 'SaaS & Software', icon: 'Code' },
    { value: 'ecommerce', label: 'E-commerce', icon: 'ShoppingCart' },
    { value: 'fintech', label: 'FinTech', icon: 'DollarSign' },
    { value: 'healthcare', label: 'Healthcare', icon: 'Heart' },
    { value: 'education', label: 'Education', icon: 'BookOpen' },
    { value: 'marketing', label: 'Marketing', icon: 'TrendingUp' }
  ];

  const emergingTrends = [
    {
      title: 'AI-Powered Automation',
      impact: 'High',
      growth: '+245%',
      description: 'Businesses adopting AI automation see 40% productivity increase',
      color: 'border-success'
    },
    {
      title: 'Subscription Model Evolution',
      impact: 'Medium',
      growth: '+128%',
      description: 'Flexible pricing and usage-based billing gaining traction',
      color: 'border-primary'
    },
    {
      title: 'Remote-First Operations',
      impact: 'High',
      growth: '+187%',
      description: 'Distributed teams becoming the new standard across industries',
      color: 'border-warning'
    },
    {
      title: 'Data Privacy Regulations',
      impact: 'Critical',
      growth: '+95%',
      description: 'Compliance requirements driving infrastructure changes',
      color: 'border-error'
    }
  ];

  const handleResearchTrends = async () => {
    setLoading(true);
    try {
      const industryLabel = industries?.find(i => i?.value === selectedIndustry)?.label;
      const result = await perplexityService?.searchWeb(
        `Latest industry trends and emerging opportunities in ${industryLabel} for 2026. Include market size, growth projections, key players, and actionable insights for businesses.`,
        { searchDepth: 'high', maxTokens: 3000 }
      );
      
      setTrendInsights(result);
      toast?.success('Industry trends updated');
    } catch (error) {
      console.error('Trends research error:', error);
      toast?.error('Failed to fetch industry trends');
    } finally {
      setLoading(false);
    }
  };

  const getImpactColor = (impact) => {
    const colors = {
      'Critical': 'text-error',
      'High': 'text-success',
      'Medium': 'text-warning',
      'Low': 'text-muted-foreground'
    };
    return colors?.[impact] || colors?.Low;
  };

  return (
    <div className="space-y-6">
      {/* Industry Selection */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Compass" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Select Industry</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {industries?.map((industry) => (
            <button
              key={industry?.value}
              onClick={() => setSelectedIndustry(industry?.value)}
              className={`
                flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-250
                ${selectedIndustry === industry?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }
              `}
            >
              <Icon name={industry?.icon} size={20} color={selectedIndustry === industry?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
              <span className={`font-medium ${selectedIndustry === industry?.value ? 'text-primary' : 'text-foreground'}`}>
                {industry?.label}
              </span>
            </button>
          ))}
        </div>
        <Button
          onClick={handleResearchTrends}
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {loading ? (
            <>
              <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
              Researching Trends...
            </>
          ) : (
            <>
              <Icon name="Search" size={18} className="mr-2" />
              Research Latest Trends
            </>
          )}
        </Button>
      </div>

      {/* AI-Generated Insights */}
      {trendInsights && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Sparkles" size={24} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-foreground">AI-Powered Market Intelligence</h3>
          </div>
          <div className="prose prose-sm max-w-none text-foreground mb-4">
            <p className="whitespace-pre-wrap">{trendInsights?.content}</p>
          </div>
          {trendInsights?.sources?.length > 0 && (
            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium text-muted-foreground mb-2">Research Sources:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {trendInsights?.sources?.map((source, index) => (
                  <a
                    key={index}
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-2"
                  >
                    <Icon name="ExternalLink" size={14} />
                    Source {index + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Emerging Trends */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Emerging Trends</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {emergingTrends?.map((trend) => (
            <div key={trend?.title} className={`border-2 ${trend?.color} rounded-xl p-4 hover:shadow-lg transition-all duration-250`}>
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-foreground">{trend?.title}</h4>
                <span className={`text-xs font-bold px-2 py-1 rounded ${getImpactColor(trend?.impact)} bg-current/10`}>
                  {trend?.impact}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{trend?.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Growth Rate</span>
                <span className="text-sm font-bold text-success">{trend?.growth}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Opportunities */}
      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Target" size={24} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">Strategic Opportunities</h3>
        </div>
        <div className="space-y-4">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
              <h4 className="font-semibold text-success">High Priority</h4>
            </div>
            <p className="text-sm text-foreground">Invest in AI automation capabilities to stay competitive and improve operational efficiency by 40%</p>
          </div>
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertCircle" size={18} color="var(--color-warning)" />
              <h4 className="font-semibold text-warning">Medium Priority</h4>
            </div>
            <p className="text-sm text-foreground">Explore flexible pricing models to capture emerging market segments and increase customer acquisition</p>
          </div>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Info" size={18} color="var(--color-primary)" />
              <h4 className="font-semibold text-primary">Long-term Strategy</h4>
            </div>
            <p className="text-sm text-foreground">Build remote-first infrastructure and processes to attract global talent and reduce operational costs</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryTrends;